import { boxCenter, type DetectionClass } from './Detector';
import type { TrackedObject } from './Tracker';

export interface TrajectoryPoint {
  t: number;
  x: number;
  y: number;
}

export interface Trajectory {
  id: number;
  class: DetectionClass;
  points: TrajectoryPoint[];
}

export interface TrajectoryStoreOptions {
  maxPoints?: number;
}

function trimOverflow<T>(items: T[], maxItems: number): void {
  const overflow = items.length - maxItems;
  if (overflow > 0) items.splice(0, overflow);
}

export class TrajectoryStore {
  private readonly histories = new Map<number, Trajectory>();
  private maxPoints: number;
  private snapshot: Trajectory[] = [];
  private snapshotDirty = false;

  constructor(opts: TrajectoryStoreOptions = {}) {
    this.maxPoints = opts.maxPoints ?? 90;
  }

  reset(): void {
    this.histories.clear();
    this.snapshot = [];
    this.snapshotDirty = false;
  }

  setMaxPoints(n: number): void {
    const next = Math.max(2, Math.floor(n));
    for (const traj of this.histories.values()) {
      trimOverflow(traj.points, next);
    }
    this.maxPoints = next;
  }

  append(tracks: readonly TrackedObject[], now: number): void {
    for (const t of tracks) {
      let traj = this.histories.get(t.id);
      if (!traj) {
        traj = { id: t.id, class: t.class, points: [] };
        this.histories.set(t.id, traj);
        this.snapshotDirty = true;
      }
      const c = boxCenter(t.box);
      traj.points.push({ t: now, x: c.x, y: c.y });
      trimOverflow(traj.points, this.maxPoints);
    }
  }

  drop(ids: readonly number[]): void {
    for (const id of ids) {
      if (this.histories.delete(id)) this.snapshotDirty = true;
    }
  }

  getAll(): readonly Trajectory[] {
    if (this.snapshotDirty) {
      this.snapshot = Array.from(this.histories.values());
      this.snapshotDirty = false;
    }
    return this.snapshot;
  }

  get(id: number): Trajectory | undefined {
    return this.histories.get(id);
  }
}
